import { DataResult, DataSourceRequestState } from '@progress/kendo-data-query';
import { endOfDay, sub } from 'date-fns';
import {
  ProductDto,
  ProductsByStatusDto,
  ProductsCreatedDto,
  ProductStatusDto
} from 'src/store/products/product';
import { ArrayUtils } from 'src/utils/array';
import { ApiError } from '../api-handler';
import { ParameterSetDetailDto } from '../parametersets/parameterset';
import { ProductAction, ProductTypes } from './products.actions';

export type ProductsState = Readonly<{
  activeTab: number;
  activeParameterSet: Nullable<ParameterSetDetailDto>;
  products: ProductDto[];
  filteredProducts: DataResult;
  filteredProductParameters: DataResult;
  productStatuses: ProductStatusDto[];
  productsCreated: ProductsCreatedDto[];
  productsByStatus: Nullable<ProductsByStatusDto>;
  errors: Nullable<ApiError>;
  requestState: DataSourceRequestState;
}>;

export const initialState: ProductsState = {
  activeTab: 0,
  activeParameterSet: null,
  products: [],
  filteredProducts: { data: [], total: 0 },
  filteredProductParameters: { data: [], total: 0 },
  productStatuses: [],
  productsCreated: [],
  productsByStatus: null,
  errors: null,
  requestState: {
    skip: 0,
    take: 20,
    sort: [{ field: 'runDate', dir: 'desc' }],
    filter: {
      logic: 'and',
      filters: [
        {
          field: 'runDate',
          value: sub(new Date(), {
            months: 6
          }),
          operator: 'gte'
        },
        {
          field: 'runDate',
          value: endOfDay(new Date()),
          operator: 'lte'
        }
      ]
    }
  }
};

const productsReducer = (
  state: ProductsState = initialState,
  action: ProductAction
): ProductsState => {
  switch (action.type) {
    case ProductTypes.SetActiveTab:
      return { ...state, activeTab: action.tab };
    case ProductTypes.FetchActiveParameterSet:
      return { ...state, activeParameterSet: action.parameterSet };
    case ProductTypes.FetchAll:
      return { ...state, products: action.products };
    case ProductTypes.FetchFiltered:
      return { ...state, filteredProducts: action.filteredProducts };
    case ProductTypes.FetchFilteredParameters:
      return {
        ...state,
        filteredProductParameters: action.filteredProductParameters
      };
    case ProductTypes.FetchStatuses:
      return { ...state, productStatuses: action.productStatuses };
    case ProductTypes.Delete:
      return {
        ...state,
        products: ArrayUtils.deleteFromListById(state.products, action.product),
        filteredProducts: {
          ...state.filteredProducts,
          data: ArrayUtils.deleteFromListById(
            state.filteredProducts.data,
            action.product
          )
        }
      };
    case ProductTypes.FetchProductsByStatus:
      return { ...state, productsByStatus: action.productsByStatus };
    case ProductTypes.FetchProductsCreated:
      return { ...state, productsCreated: action.productsCreated };
    case ProductTypes.Failure:
      return { ...state, errors: action.errors };
    case ProductTypes.UpdateRequestState:
      return { ...state, requestState: action.requestState };
    default:
      return state;
  }
};

export default productsReducer;
